import React from 'react'
import { Collapse, Result, Space } from 'antd'
import Answer from './answer/Answer'
import { useDispatch, useSelector } from 'react-redux'
import { textSubmissionDrawerActions } from '../../../../../../store/slices/text-submission-drawer.slice'
import textSubmissionDrawerSelectors from '../../../../../../store/selectors/text-submission-drawer.selectors'
import assignmentSelectors from '../../../../../../store/selectors/assignment.selectors'
import { useFetchTextSubmissionDetailsQuery } from '../../../../../../store/apis/submission.api'
import {
    useQuestionFocusNavigationShortcuts,
    useScrollToFirstEscalatedOnMount,
    useScrollToFocusedQuestionRubric,
} from '../../../../../../store/apis/utils/hooks/text-submission.hooks'
import AssigneeList from './AssigneeList'
import useRubricContext from '../../../../../../hooks/useRubricContext'

const Answers = ({ submissionId }) => {
    const expandedQuestionIds = useSelector(textSubmissionDrawerSelectors.expandedQuestionIds)
    const focusedQuestionRubricId = useSelector(textSubmissionDrawerSelectors.focusedQuestionRubricId)
    const isOwner = useSelector(assignmentSelectors.isOwner)
    const dispatch = useDispatch()
    
    // Use enriched questions from useRubricContext (includes full rubric data)
    const { questions } = useRubricContext()
    
    const {
        data: submission,
        isFetching: isFetchingSubmission,
        error: errorFetchingSubmission,
    } = useFetchTextSubmissionDetailsQuery({ submissionId })

    useQuestionFocusNavigationShortcuts()
    useScrollToFirstEscalatedOnMount(submissionId)
    useScrollToFocusedQuestionRubric(isFetchingSubmission)

    const items = questions?.map(question => ({
        key: question.id,
        label: <Space>{question?.name || question?.content}</Space>,
        children: (
            <Answer
                submissionId={submissionId}
                answers={submission?.answers}
                loading={isFetchingSubmission}
                question={question}
            />
        ),
        extra: isOwner ? <AssigneeList question={question} submission={submission} /> : null,
    }))

    const onChange = expandedQuestionKeys => {
        const numericKeys = expandedQuestionKeys.map(Number)
        dispatch(textSubmissionDrawerActions.setExpandedQuestionIds(numericKeys))

        // Check if the currently focused questionRubric's parent question is being collapsed
        if (focusedQuestionRubricId) {
            const focusedQuestion = questions?.find(q => 
                q.questionRubrics?.some(qr => qr.id === focusedQuestionRubricId)
            )
            const parentQuestionBeingCollapsed = focusedQuestion && !numericKeys.includes(focusedQuestion.id)
            
            if (parentQuestionBeingCollapsed) {
                dispatch(textSubmissionDrawerActions.setFocusedQuestionRubricId(null))
            }
        }

        // Only set focused question if there are expanded questions
        if (numericKeys.length > 0) {
            const lastOpenedQuestion = questions.find(question => question.id === numericKeys.at(-1))
            if (lastOpenedQuestion) {
                // Focus the first questionRubric of the expanded question
                const focusId = lastOpenedQuestion.questionRubrics?.[0]?.id
                if (focusId) {
                    dispatch(textSubmissionDrawerActions.setFocusedQuestionRubricId(focusId))
                }
            }
        } else {
            // Clear focused question when all are collapsed to prevent auto-reopening
            dispatch(textSubmissionDrawerActions.setFocusedQuestionRubricId(null))
        }
    }
    if (errorFetchingSubmission) return <Result status='error' title='Submission Fetch Failed' />
    return <Collapse activeKey={expandedQuestionIds} onChange={onChange} items={items} size='small' bordered={false}/>
}

export default Answers
